// NetherStore.jsx
// THIS IS YOUR MAIN FILE - DON'T EDIT THIS FILE
// ONLY EDIT storeData.js TO ADD/REMOVE VIDEOS

import React, { useState, useEffect } from 'react';
import { Search, Youtube, Sparkles, Star, Award, Zap, X } from 'lucide-react';
import { STORE_DATA, PARTNER_CURATED_MODS } from './storeData';

// Partner's mod feed - auto-updates whenever they add new mods on their end
const PARTNER_FEED_URL = 'https://blumeplugins.co/all-mods.json';

// Lets /partner open straight to the Partner Mods tab (and / to your own).
const getSectionFromPath = () =>
  window.location.pathname.replace(/\/+$/, '') === '/partner' ? 'partners' : 'mine';

const NetherStore = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeSection, setActiveSection] = useState(getSectionFromPath);
  const [partnerVideos, setPartnerVideos] = useState([]);
  const [partnerLoaded, setPartnerLoaded] = useState(false);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [partnerError, setPartnerError] = useState(false);

  const switchSection = (section) => {
    setActiveSection(section);
    const path = section === 'partners' ? '/partner' : '/';
    if (window.location.pathname !== path) {
      window.history.pushState(null, '', path);
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const handlePopState = () => setActiveSection(getSectionFromPath());
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Fetched once on load (not gated on the tab) so search can scan partner
  // mods even before the Partner Mods tab has ever been opened.
  useEffect(() => {
    setPartnerLoading(true);
    setPartnerError(false);

    fetch(PARTNER_FEED_URL)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch partner mods');
        return res.json();
      })
      .then((data) => {
        const videos = (data.videos || []).map((video, index) => ({
          ...video,
          id: index + 1
        }));
        setPartnerVideos(videos);
        setPartnerLoaded(true);
      })
      .catch(() => setPartnerError(true))
      .finally(() => setPartnerLoading(false));
  }, []);

  const getYouTubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : 'https://via.placeholder.com/300x200/8b5cf6/ffffff?text=No+Thumbnail';
  };

  const NEW_BADGE_COUNT = 3;

  const myVideos = STORE_DATA.videos.map((video, index) => ({
    ...video,
    id: `mine-${index + 1}`,
    source: 'mine',
    isNew: index < NEW_BADGE_COUNT
  }));

  const curatedPartnerVideos = PARTNER_CURATED_MODS.map((video, index) => ({
    ...video,
    id: `partner-curated-${index + 1}`,
    source: 'partner'
  }));

  const fetchedPartnerVideos = partnerVideos.map((video, index) => ({
    ...video,
    id: `partner-feed-${index + 1}`,
    source: 'partner'
  }));

  const taggedPartnerVideos = [...fetchedPartnerVideos, ...curatedPartnerVideos];

  const isSearching = searchTerm.trim().length > 0;

  // A search scans both catalogs so a client finds a mod regardless of
  // which tab they're on; browsing with an empty search stays per-tab.
  const activeVideos = isSearching
    ? [...myVideos, ...taggedPartnerVideos]
    : (activeSection === 'mine' ? myVideos : taggedPartnerVideos);

  const filteredVideos = activeVideos.filter(video =>
    video.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Custom Order Button Handler - Edit the Discord username here
  const handleCustomOrder = () => {
    setShowOrderModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 text-white relative overflow-hidden">
      {/* Animated background particles */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-20 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Cursor glow effect */}
      <div 
        className="fixed w-96 h-96 bg-gradient-radial from-cyan-500/20 via-purple-500/10 to-transparent rounded-full pointer-events-none blur-3xl transition-all duration-300 ease-out"
        style={{
          left: mousePosition.x - 192,
          top: mousePosition.y - 192,
        }}
      ></div>

      <style>
        {`
          @keyframes scroll-infinite {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-scroll-infinite {
            display: flex;
            animation: scroll-infinite 25s linear infinite;
            white-space: nowrap;
          }
          .animate-scroll-infinite:hover {
            animation-play-state: paused;
          }
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          .animate-shimmer {
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
            background-size: 200% 100%;
            animation: shimmer 2s infinite;
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
          @keyframes gradient-shift {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          .animate-gradient {
            background-size: 200% 200%;
            animation: gradient-shift 8s ease infinite;
          }
          @keyframes glow-pulse {
            0%, 100% { box-shadow: 0 0 20px rgba(147, 51, 234, 0.4), 0 0 40px rgba(147, 51, 234, 0.2); }
            50% { box-shadow: 0 0 30px rgba(147, 51, 234, 0.6), 0 0 60px rgba(147, 51, 234, 0.3); }
          }
          .glow-effect {
            animation: glow-pulse 3s ease-in-out infinite;
          }
          @keyframes rotate-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-rotate-slow {
            animation: rotate-slow 20s linear infinite;
          }
        `}
      </style>
      
      <header className="container mx-auto px-6 py-8 relative z-10">
        {/* Premium badge at the top */}
        <div className="flex justify-center mb-6 px-2">
          <div className="inline-flex flex-wrap items-center justify-center gap-2 px-4 sm:px-6 py-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-400/50 rounded-full backdrop-blur-md text-center">
            <Award className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <span className="text-xs sm:text-sm font-bold bg-gradient-to-r from-amber-200 to-yellow-200 bg-clip-text text-transparent">
              Professional Grade Minecraft Development
            </span>
            <Award className="w-5 h-5 text-amber-400 flex-shrink-0" />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-8 mb-8">
          <div className="w-full lg:flex-1 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 backdrop-blur-xl border-2 border-blue-500/30 rounded-3xl p-6 shadow-2xl hover:shadow-cyan-500/40 transition-all duration-500 hover:scale-[1.02] relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-cyan-500/10 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-6 h-6 text-cyan-400" />
                <p className="text-xl md:text-2xl font-black bg-gradient-to-r from-blue-300 via-cyan-300 to-blue-300 bg-clip-text text-transparent animate-gradient">
                  {STORE_DATA.storeInfo.tagline}
                </p>
              </div>
              <p className="text-sm text-gray-300 mt-2 flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                {STORE_DATA.storeInfo.subtitle}
              </p>
              <p className="text-sm text-gray-400 mt-2">Made by: <span className="text-cyan-400 font-semibold">{STORE_DATA.storeInfo.madeBy}</span></p>
            </div>
          </div>

          <div className="w-full lg:flex-1 text-center relative order-first lg:order-none">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 blur-3xl animate-pulse"></div>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent drop-shadow-2xl relative animate-gradient">
              {STORE_DATA.storeInfo.name}
            </h1>
            <div className="flex items-center justify-center gap-2 mt-3">
              <div className="h-1 w-20 bg-gradient-to-r from-transparent via-cyan-400 to-transparent rounded-full"></div>
              <Sparkles className="w-5 h-5 text-cyan-400 animate-pulse" />
              <div className="h-1 w-20 bg-gradient-to-r from-transparent via-cyan-400 to-transparent rounded-full"></div>
            </div>
          </div>

          <div className="w-full lg:flex-1 bg-gradient-to-l from-indigo-600/20 to-cyan-600/20 backdrop-blur-xl border-2 border-indigo-500/30 rounded-3xl p-5 shadow-2xl hover:shadow-indigo-500/40 transition-all duration-500 overflow-hidden hover:scale-[1.02] relative group">
            <div className="absolute inset-0 bg-gradient-to-l from-indigo-500/0 via-purple-500/10 to-indigo-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative">
              <p className="text-xs text-gray-300 mb-3 text-center uppercase tracking-wider font-bold flex items-center justify-center gap-2">
                <Star className="w-4 h-4 text-indigo-400 fill-indigo-400" />
                Trusted By Industry Leaders
                <Star className="w-4 h-4 text-indigo-400 fill-indigo-400" />
              </p>
              <div className="relative w-full overflow-hidden" style={{height: '95px'}}>
                <div className="animate-scroll-infinite" style={{paddingTop: '2px'}}>
                  {STORE_DATA.clients.concat(STORE_DATA.clients).map((client, idx) => (
                    <a
                      key={idx}
                      href={client.channel}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mx-4 group/client"
                      style={{verticalAlign: 'top'}}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-cyan-500 rounded-full blur-md opacity-0 group-hover/client:opacity-70 transition-opacity"></div>
                          <img 
                            src={client.image}
                            alt={client.name}
                            className="w-16 h-16 rounded-full border-2 border-indigo-500/60 group-hover/client:border-cyan-400 transition-all duration-300 object-cover group-hover/client:scale-110 shadow-lg relative"
                          />
                        </div>
                        <div className="px-3 py-1.5 bg-gradient-to-r from-indigo-600/60 to-cyan-600/60 border border-indigo-400/60 group-hover/client:border-cyan-400 rounded-full transition-all duration-300" style={{minWidth: '70px'}}>
                          <p className="text-xs font-bold text-cyan-200 whitespace-nowrap text-center leading-none">
                            {client.subs}
                          </p>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CUSTOM ORDER BUTTON - Enhanced premium version */}
        <div className="max-w-4xl mx-auto mb-8">
          <button
            onClick={handleCustomOrder}
            onMouseEnter={() => setIsButtonHovered(true)}
            onMouseLeave={() => setIsButtonHovered(false)}
            className="w-full relative overflow-hidden bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 hover:from-purple-500 hover:via-pink-500 hover:to-purple-500 border-2 border-purple-400/50 hover:border-pink-400 rounded-3xl p-8 shadow-2xl glow-effect transition-all duration-500 transform hover:scale-[1.03] animate-float group"
          >
            <div className={`absolute inset-0 ${isButtonHovered ? 'animate-shimmer' : ''}`}></div>
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-pink-500/20 to-purple-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Decorative corner stars */}
            <Star className="absolute top-4 left-4 w-6 h-6 text-yellow-300 fill-yellow-300 animate-pulse opacity-70" />
            <Star className="absolute top-4 right-4 w-6 h-6 text-yellow-300 fill-yellow-300 animate-pulse opacity-70" style={{ animationDelay: '0.5s' }} />
            <Star className="absolute bottom-4 left-4 w-6 h-6 text-yellow-300 fill-yellow-300 animate-pulse opacity-70" style={{ animationDelay: '1s' }} />
            <Star className="absolute bottom-4 right-4 w-6 h-6 text-yellow-300 fill-yellow-300 animate-pulse opacity-70" style={{ animationDelay: '1.5s' }} />
            
            <div className="relative flex items-center justify-center gap-4">
              <Sparkles className="w-10 h-10 text-yellow-300 animate-pulse" />
              <div className="text-center">
                <h3 className="text-3xl md:text-4xl font-black bg-gradient-to-r from-yellow-200 via-pink-200 to-yellow-200 bg-clip-text text-transparent animate-gradient">
                  Order Custom Mods
                </h3>
                <p className="text-base md:text-lg text-purple-100 mt-2 font-medium">
                  Have a unique idea? We'll make it reality! Contact us on Discord
                </p>
              </div>
              <Sparkles className="w-10 h-10 text-yellow-300 animate-pulse" />
            </div>
          </button>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-cyan-400 z-10 transition-all duration-300 group-hover:scale-110" size={24} />
            <input
              type="text"
              placeholder="Search for mods and modpacks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-14 pr-14 py-5 bg-slate-800/60 backdrop-blur-xl border-2 border-blue-500/40 rounded-3xl focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 text-white placeholder-gray-400 transition-all shadow-2xl hover:shadow-blue-500/40 relative"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
                className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors z-10"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-6 relative z-10">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-black mb-3 bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent animate-gradient inline-block">
            {isSearching ? 'Search Results' : (activeSection === 'mine' ? "RoAsTy's Mods" : 'Partner Mods')}
          </h2>
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="h-1 w-16 bg-gradient-to-r from-transparent via-cyan-400 to-transparent rounded-full"></div>
            <Zap className="w-4 h-4 text-cyan-400" />
            <div className="h-1 w-16 bg-gradient-to-r from-transparent via-cyan-400 to-transparent rounded-full"></div>
          </div>

          {isSearching ? (
            <p className="text-sm text-gray-400">
              Searching RoAsTy's Mods & Partner Mods
              {partnerLoading && !partnerLoaded && ' — still checking partner catalog...'}
            </p>
          ) : (
            <div className="inline-flex items-center gap-2 p-1.5 bg-slate-800/60 backdrop-blur-xl border-2 border-blue-500/30 rounded-full shadow-xl">
              <button
                onClick={() => switchSection('mine')}
                className={`px-6 py-2.5 rounded-full font-bold text-sm transition-all duration-300 ${
                  activeSection === 'mine'
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-500/40'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                RoAsTy's Mods
              </button>
              <button
                onClick={() => switchSection('partners')}
                className={`px-6 py-2.5 rounded-full font-bold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeSection === 'partners'
                    ? 'bg-gradient-to-r from-pink-600 to-purple-500 text-white shadow-lg shadow-pink-500/40'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Sparkles className="w-4 h-4" />
                Partner Mods
              </button>
            </div>
          )}

          {!isSearching && activeSection === 'partners' && (
            <p className="text-sm text-gray-400 mt-4">
              Quality mods crafted by fellow developers we trust
              {partnerLoading && !partnerLoaded && ' — loading more...'}
              {partnerError && ' — some partner mods failed to load'}
            </p>
          )}
        </div>

        {filteredVideos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-5">
            {filteredVideos.map((video) => (
              <a
                key={video.id}
                href={video.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => setHoveredVideo(video.id)}
                onMouseLeave={() => setHoveredVideo(null)}
                className="group cursor-pointer block"
              >
                <div className={`bg-slate-800/60 backdrop-blur-xl rounded-2xl overflow-hidden border-2 border-blue-500/40 transition-all duration-500 shadow-2xl relative ${
                  hoveredVideo === video.id 
                    ? 'scale-105 shadow-2xl shadow-cyan-500/60 -translate-y-3 border-cyan-500/80' 
                    : 'scale-100 hover:border-blue-500/60'
                }`}>
                  {/* Premium glow effect on hover */}
                  {hoveredVideo === video.id && (
                    <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/20 via-transparent to-purple-500/20 pointer-events-none"></div>
                  )}
                  
                  <div className="relative overflow-hidden aspect-video">
                    <img 
                      src={getYouTubeThumbnail(video.videoUrl)} 
                      alt={video.title}
                      className={`w-full h-full object-cover transition-transform duration-500 ${
                        hoveredVideo === video.id ? 'scale-110' : 'scale-100'
                      }`}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x200/8b5cf6/ffffff?text=Video+Thumbnail';
                      }}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent transition-opacity duration-300 ${
                      hoveredVideo === video.id ? 'opacity-100' : 'opacity-70'
                    }`}></div>
                    
                    {hoveredVideo === video.id && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-gradient-to-r from-red-600 to-red-500 rounded-full p-4 animate-pulse shadow-2xl shadow-red-500/50">
                          <Youtube size={32} />
                        </div>
                      </div>
                    )}

                    <div className="absolute top-3 left-3 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-xl flex items-center gap-1">
                      <Star className="w-3 h-3 fill-white" />
                      {video.badge}
                    </div>

                    {video.isNew && (
                      <div className="absolute top-3 right-3 bg-gradient-to-r from-pink-600 to-rose-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-xl shadow-pink-500/40 flex items-center gap-1 animate-pulse">
                        <Sparkles className="w-3 h-3" />
                        New
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <h3 className={`font-bold text-base transition-colors duration-300 line-clamp-2 min-h-[48px] ${
                      hoveredVideo === video.id ? 'text-cyan-400' : 'text-white'
                    }`}>
                      {video.title}
                    </h3>
                    {video.version && (
                      <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                        <Zap className="w-3 h-3 text-cyan-400" />
                        {video.version}
                      </p>
                    )}

                    <div className="flex items-center gap-2 mt-4">
                      {video.source === 'mine' ? (
                        <>
                          <div className="px-3 py-2 bg-gradient-to-r from-emerald-600/30 to-emerald-500/30 border border-emerald-500/60 rounded-xl text-emerald-400 font-bold text-xs shadow-lg">
                            {video.price}
                          </div>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedVideo(video);
                              setShowBuyModal(true);
                            }}
                            className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 rounded-xl font-bold transition-all duration-300 text-xs shadow-lg hover:shadow-blue-500/50"
                          >
                            Buy Now
                          </button>
                        </>
                      ) : (
                        <div className="flex-1 px-3 py-2 bg-gradient-to-r from-pink-600/30 to-purple-500/30 border border-pink-500/60 rounded-xl text-pink-300 font-bold text-xs shadow-lg text-center flex items-center justify-center gap-1">
                          <Youtube className="w-3.5 h-3.5" />
                          Watch on YouTube
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="inline-block p-8 bg-slate-800/60 backdrop-blur-xl rounded-3xl border-2 border-blue-500/30">
              <p className="text-gray-400 text-xl">No videos found matching "{searchTerm}"</p>
            </div>
          </div>
        )}
      </main>

      <footer className="container mx-auto px-6 py-10 text-center text-gray-400 border-t border-blue-500/30 mt-16 relative z-10 backdrop-blur-xl">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Star className="w-5 h-5 text-cyan-400 fill-cyan-400 animate-pulse" />
          <p className="text-lg font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            © 2024 {STORE_DATA.storeInfo.name}
          </p>
          <Star className="w-5 h-5 text-cyan-400 fill-cyan-400 animate-pulse" />
        </div>
        <p className="text-sm">Professional Minecraft Mod Development</p>
      </footer>
      {/* Buy Now Modal */}
      {showBuyModal && selectedVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowBuyModal(false)}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>

          <div
            className="relative w-full max-w-md bg-gradient-to-br from-slate-900 via-blue-900/40 to-slate-900 border-2 border-blue-500/60 rounded-3xl shadow-2xl shadow-blue-500/20 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-1 w-full bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-500"></div>

            <div className="absolute top-0 right-0 w-48 h-48 bg-blue-600/10 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-600/10 rounded-full blur-3xl pointer-events-none"></div>

            <div className="relative p-8">
              {/* Mod title & badge */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center shadow-xl shadow-blue-500/30 flex-shrink-0">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="text-xs text-blue-400 uppercase tracking-widest font-bold mb-1">You're purchasing</p>
                  <h2 className="text-xl font-black text-white leading-tight">{selectedVideo.title}</h2>
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                    <Zap className="w-3 h-3 text-cyan-400" />{selectedVideo.version}
                  </p>
                </div>
              </div>

              {/* Price tag */}
              <div className="flex items-center justify-between bg-slate-800/80 border border-blue-500/30 rounded-2xl px-5 py-4 mb-6">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Price</p>
                  <p className="text-3xl font-black bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">{selectedVideo.price}</p>
                </div>
                <div className="text-right">
                  <div className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500/20 border border-emerald-500/40 rounded-full">
                    <Star className="w-3 h-3 text-emerald-400 fill-emerald-400" />
                    <span className="text-xs text-emerald-400 font-bold">{selectedVideo.badge}</span>
                  </div>
                </div>
              </div>

              {/* How to buy */}
              <div className="bg-slate-800/60 border border-indigo-500/30 rounded-2xl p-5 mb-6">
                <p className="text-xs text-gray-400 uppercase tracking-wider font-bold mb-3 flex items-center gap-2">
                  <Award className="w-3 h-3 text-indigo-400" /> How to Purchase
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-600/40 border border-blue-500/60 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-black text-blue-300">1</span>
                    </div>
                    <p className="text-sm text-gray-300">Open Discord and search for <span className="text-white font-bold">RoAstY69</span></p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-600/40 border border-blue-500/60 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-black text-blue-300">2</span>
                    </div>
                    <p className="text-sm text-gray-300">Send the mod name & your Minecraft version</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-600/40 border border-blue-500/60 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-black text-blue-300">3</span>
                    </div>
                    <p className="text-sm text-gray-300">Receive your mod file after payment — fast delivery!</p>
                  </div>
                </div>
              </div>

              {/* Discord contact */}
              <div className="flex items-center gap-3 bg-[#5865F2]/15 border border-[#5865F2]/40 rounded-2xl px-4 py-3 mb-6">
                <svg className="w-5 h-5 text-[#5865F2] flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                <p className="text-sm text-gray-300">Message <span className="text-white font-black">RoAstY69</span> on Discord to complete your order</p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBuyModal(false)}
                  className="flex-1 py-3 bg-slate-700/80 hover:bg-slate-600/80 border border-slate-600 rounded-2xl font-bold text-gray-300 hover:text-white transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { window.open('https://discord.com', '_blank'); setShowBuyModal(false); }}
                  className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 rounded-2xl font-black text-white shadow-lg shadow-blue-500/40 transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                  Contact on Discord
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Order Modal */}
      {showOrderModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowOrderModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>

          {/* Modal Card */}
          <div 
            className="relative w-full max-w-md bg-gradient-to-br from-slate-900 via-purple-900/60 to-slate-900 border-2 border-purple-500/60 rounded-3xl shadow-2xl shadow-purple-500/30 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top glow bar */}
            <div className="h-1 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500"></div>

            {/* Decorative blobs */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-purple-600/20 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-cyan-600/20 rounded-full blur-3xl pointer-events-none"></div>

            <div className="relative p-8">
              {/* Icon */}
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center shadow-xl shadow-purple-500/40 glow-effect">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
              </div>

              {/* Title */}
              <h2 className="text-3xl font-black text-center bg-gradient-to-r from-purple-300 via-pink-300 to-yellow-200 bg-clip-text text-transparent mb-2">
                Custom Mod Orders
              </h2>
              <p className="text-center text-gray-400 text-sm mb-7">Have a unique idea? We'll make it reality!</p>

              {/* Discord contact card */}
              <div className="bg-slate-800/80 border border-indigo-500/40 rounded-2xl p-5 mb-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-[#5865F2] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1">Contact on Discord</p>
                  <p className="text-xl font-black text-white">RoAstY69</p>
                </div>
              </div>

              <p className="text-center text-gray-300 text-sm mb-7">
                We can bring <span className="text-cyan-400 font-bold">any Minecraft concept</span> to life!
              </p>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowOrderModal(false)}
                  className="flex-1 py-3 bg-slate-700/80 hover:bg-slate-600/80 border border-slate-600 rounded-2xl font-bold text-gray-300 hover:text-white transition-all duration-300"
                >
                  Close
                </button>
                <button
                  onClick={() => { window.open('https://discord.com', '_blank'); setShowOrderModal(false); }}
                  className="flex-1 py-3 bg-gradient-to-r from-[#5865F2] to-indigo-600 hover:from-indigo-500 hover:to-[#5865F2] rounded-2xl font-bold text-white shadow-lg shadow-indigo-500/40 transition-all duration-300 hover:scale-105"
                >
                  Open Discord
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetherStore;